import {
    ChangeDetectionStrategy,
    Component,
    forwardRef,
    Input,
    OnInit,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { TimeService, utils } from '@bayclubs/utils';
import moment from 'moment-timezone';
import {
    CalendarSettings,
    ICalendarDay,
} from '../../models/date-picker-multiselect.model';

const noop = () => {};
export const CUSTOM_INPUT_CONTROL_VALUE_ACCESSOR: any = {
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => DatePickerMultiselectComponent),
    multi: true,
};

@Component({
    selector: 'bcu-date-picker-multiselect',
    templateUrl: './date-picker-multiselect.component.html',
    styleUrls: ['./date-picker-multiselect.component.scss'],
    providers: [CUSTOM_INPUT_CONTROL_VALUE_ACCESSOR],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class DatePickerMultiselectComponent
    implements OnInit, ControlValueAccessor
{
    _settings: CalendarSettings;
    public month: moment.Moment;
    // public date: ICalendarDay;
    public days: Array<ICalendarDay> = null;
    private momentsInner: moment.Moment[] = [];

    @Input() public readonly: boolean = false;
    @Input() public isRequired: boolean = false;

    onChange = (dates: utils.BcDate[]) => {};
    onTouched = () => {};

    registerOnChange(fn: (dates: utils.BcDate[]) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    writeValue(dates: utils.BcDate[]): void {
        this.momentsInner = dates
            .filter((x) => x)
            .map((x) => moment(x.value))
            .filter((x) => x.isValid());
        this.onChange(this.value);

        if (this.momentsInner.length > 0) {
            this.month = this.time.startOfMonth(this.momentsInner[0]);
            this.setupDays();
        }
    }
    get value(): utils.BcDate[] {
        const v = this.momentsInner
            .filter((x) => x.isValid())
            .map((x) => this.time.toBcDate(x));
        return v;
    }

    constructor(private time: TimeService) {
        this.month = this.time.startOfMonth(this.getCurrentDate());
    }

    ngOnInit() {
        this.setupDays();
    }

    get settings(): CalendarSettings {
        if (this._settings) return this._settings;

        this._settings = this.getDefaultCalendarSettings();
        return this._settings;
    }

    set settings(settings: CalendarSettings) {
        this._settings = settings;
    }

    public getDefaultCalendarSettings(): CalendarSettings {
        var defSettings = new CalendarSettings();
        defSettings.activeClass = 'day-active';

        defSettings.notActive = 'day-not-active';

        return defSettings;
    }

    public onSelectClicked(day: ICalendarDay) {
        if (this.readonly) {
            return;
        }

        var existing = this.indexOfDate(day.date);
        if (existing >= 0) {
            this.momentsInner.splice(existing, 1);
        } else {
            this.momentsInner.push(day.date);
        }
        this.onChange(this.value);
    }

    public isSelected(day: ICalendarDay): boolean {
        if (!this.momentsInner) {
            return false;
        }
        var thesame = this.indexOfDate(day.date);
        return thesame >= 0;
    }

    public indexOfDate(date: moment.Moment) {
        var theSame = this.momentsInner.findIndex((x) => x.isSame(date, 'day'));

        return theSame;
    }

    public getDayClass() {
        return this.settings.activeClass;
    }

    public onPreviousMonthClicked(): void {
        this.month = this.time.addMonths(this.month, -1);
        this.setupDays();
    }

    public onNextMonthClicked(): void {
        this.month = this.time.addMonths(this.month, 1);
        this.setupDays();
    }

    public onPreviousYearClicked(): void {
        this.month = this.time.addYears(this.month, -1);
        this.setupDays();
    }

    public onNextYearClicked(): void {
        this.month = this.time.addYears(this.month, 1);
        this.setupDays();
    }

    private setupDays() {
        this.days = [];

        var startDay = this.time.startOfMonth(this.month);
        var endDay = this.time.endOfMonth(this.month);

        // console.log(this.month.month())

        //days not in month before
        var dayWeek = startDay.day();
        while (dayWeek > 0) {
            var temp = this.time.addDays(startDay, -dayWeek);
            this.days.push(this.createDay(temp, false, startDay.week()));
            dayWeek--;
        }

        //days in month
        while (startDay <= endDay) {
            var week = startDay.week();

            //handle 1st week of next month
            if (this.month.month() == 11 && week <= 2) {
                week = 53;
            }

            this.days.push(this.createDay(startDay, true, week));
            startDay = this.time.addDays(startDay, 1);
        }

        //days not in month after
        var numbersOfDays = this.days.length;

        while (numbersOfDays % 7 !== 0) {
            endDay = this.time.addDays(endDay, 1);

            //handle 1st week of next month
            week = endDay.week();
            if (this.month.month() == 11 && week <= 2) {
                week = 53;
            }

            this.days.push(this.createDay(endDay, false, week));

            numbersOfDays = this.days.length;
        }
    }

    private getCurrentDate(): moment.Moment {
        return this.time.nowUtc().startOf('day');
    }

    private createDay(
        date: moment.Moment,
        isFromCurrentMonth: boolean,
        week: number
    ): ICalendarDay {
        var day = <ICalendarDay>{
            date: date,
            isFromCurrentMonth: isFromCurrentMonth,
            week: week,
        };

        //TODO: check allowed
        return day;
    }

    public get isNotValid() {
        var notValid =
            this.isRequired &&
            (!this.momentsInner || this.momentsInner.length == 0);

        return notValid;
    }
}
